import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Tooltip } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import AgencyAPIs from "../../apiRoutes/Agency";
import { GET_AD_RECCO_QUERY_KEY, GET_AGENCIES_QUERY_KEY } from "../../utils/constants/queryKeys";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DatePicker } from "../ui/date-picker";
import AdReccoDetailModal from "./Modals/AdReccoDetailModal";
import { Download, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../utils/exportFilteredListToExcel";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import useZustandStore from "../../zustandStore/useZustandStore";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../utils/agencyScope";

const LIMIT = 20;

const AdReccoView = () => {
  const [page, setPage] = useState(0);
  const [agencyFilter, setAgencyFilter] = useState("");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const { hasPermission } = usePermissions();
  const canViewRecco =
    hasPermission(PERMISSION_CODES.RATE_CARD_VIEW) || hasPermission(PERMISSION_CODES.RATE_CARD_MANAGE);
  const canListAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, status: "approved", limit: 200, offset: 0 }),
    enabled: !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const formattedFrom = dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined;
  const formattedTo = dateTo ? format(dateTo, "yyyy-MM-dd") : undefined;

  const buildListParams = (limit, offset) => ({
    axiosPrivate,
    agency_id: agencyScoped && scopedAgencyId != null ? scopedAgencyId : agencyFilter ? Number(agencyFilter) : undefined,
    delivery_date_from: formattedFrom,
    delivery_date_to: formattedTo,
    limit,
    offset,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_AD_RECCO_QUERY_KEY, page, agencyFilter, agencyScoped, scopedAgencyId, formattedFrom, formattedTo],
    queryFn: () => AdsAPIs.listRecco(buildListParams(LIMIT, page * LIMIT)),
  });

  const rows = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  const handleExportExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    toast.loading("Preparing export…", { id: "export-excel-recco" });
    try {
      const { rows: allRows, total } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) => AdsAPIs.listRecco(buildListParams(limit, offset)),
      });
      if (!allRows.length) {
        toast.dismiss("export-excel-recco");
        toast.error("No rows to export.");
        return;
      }
      await downloadExcelFromRows({
        filename: exportFilename("ad-recco"),
        sheetName: "Recco",
        columns: [
          { header: "Ad type ID", value: (r) => r.adtype_id ?? "" },
          { header: "Ad type", value: (r) => r.adtype_name ?? "" },
          { header: "# Adset", value: (r) => r.adset_count ?? "" },
          { header: "# Ads", value: (r) => r.ad_count ?? "" },
          { header: "# Mod cycle", value: (r) => r.mod_cycle_count ?? "" },
          { header: "In mod (current/total)", value: (r) => `${r.currently_in_mod ?? 0}/${r.total_moderations ?? 0}` },
          { header: "Delivered", value: (r) => r.delivered_count ?? "" },
          { header: "Live (approved/delivered)", value: (r) => `${r.live_count ?? 0}/${r.total_for_live ?? 0}` },
          { header: "Price per unit", value: (r) => r.price_per_unit ?? "" },
          { header: "Currency", value: (r) => r.currency ?? "" },
          { header: "Cost", value: (r) => r.cost ?? "" },
        ],
        rows: allRows,
      });
      toast.dismiss("export-excel-recco");
      toast.success(`Exported ${total} row(s).`);
    } catch (e) {
      toast.dismiss("export-excel-recco");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(`Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`);
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExportingExcel(false);
    }
  };

  useEffect(() => setPage(0), [agencyFilter, dateFrom, dateTo]);

  return (
    <div>
      <div className="flex flex-col min-h-[320px] rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-200">
          <DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Delivery from" className="h-9 w-[200px] shrink-0" />
          <DatePicker value={dateTo} onChange={setDateTo} placeholder="Delivery to" className="h-9 w-[200px] shrink-0" />
          {!agencyScoped && canListAgencies && (
            <Select value={agencyFilter || "all"} onValueChange={(v) => setAgencyFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {agencies.map((a, i) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {agencyDisplayName(a, i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {canViewRecco && (
            <Tooltip title="Download Excel">
              <span className="ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-9 p-0 shrink-0"
                  aria-label="Download Excel"
                  disabled={exportingExcel || isLoading}
                  onClick={handleExportExcel}
                >
                  {exportingExcel ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[200px] gap-2">
            <ButtonLoader color="#7f7ad5" />
            <span className="text-gray-600 font-medium">Loading…</span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto flex-1 min-h-0">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600">Ad Type</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">#Adset</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">#Ads</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">#Mod Cycle</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">In Mod</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">Delivered</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-center">Live Status</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-right">Rate/Unit</th>
                    <th className="py-4 px-4 text-sm font-semibold text-gray-600 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.agency_id}-${r.adtype_id}`}
                        onClick={() => canViewRecco && setDetailRow(r)}
                        className={`hover:bg-gray-50/50 transition-colors ${canViewRecco ? "cursor-pointer" : ""}`}
                      >
                        <td className="py-4 px-4 text-sm text-gray-600">{r.adtype_name || "—"}</td>
                        <td className="py-4 px-4 text-sm text-gray-600 text-center">{r.adset_count ?? 0}</td>
                        <td className="py-4 px-4 text-sm text-gray-600 text-center">{r.ad_count ?? 0}</td>
                        <td className="py-4 px-4 text-sm text-gray-600 text-center">{r.mod_cycle_count ?? 0}</td>
                        <td className="py-4 px-4 text-sm text-center">
                          <Tooltip
                            title={`${r.currently_in_mod ?? 0} currently in moderation / ${r.total_moderations ?? 0} total moderations`}
                            placement="top"
                            arrow
                          >
                            <span className={`cursor-help ${r.currently_in_mod > 0 ? "text-amber-600 font-medium" : "text-gray-600"}`}>
                              {r.currently_in_mod ?? 0}/{r.total_moderations ?? 0}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600 text-center">{r.delivered_count ?? 0}</td>
                        <td className="py-4 px-4 text-sm text-center">
                          <Tooltip
                            title={`${r.live_count ?? 0} approved ads / ${r.total_for_live ?? 0} delivered ads`}
                            placement="top"
                            arrow
                          >
                            <span className={`cursor-help ${r.live_count > 0 ? "text-green-600 font-medium" : "text-gray-600"}`}>
                              {r.live_count ?? 0}/{r.total_for_live ?? 0}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-600 text-right">
                          {r.price_per_unit != null ? `${r.price_per_unit} ${r.currency || ""}`.trim() : "—"}
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-800 font-medium text-right">
                          {r.cost != null ? `${r.cost} ${r.currency || ""}`.trim() : "—"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-gray-400 italic">
                        No recco data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
              <span className="text-sm text-muted-foreground">
                Showing {rows.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + rows.length, totalCount)} of {totalCount}
              </span>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page * LIMIT + rows.length >= totalCount}
                  onClick={() => setPage((p) => p + 1)}
                  className="h-8"
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {detailRow && (
        <AdReccoDetailModal
          open={!!detailRow}
          onClose={() => setDetailRow(null)}
          agencyId={detailRow.agency_id}
          adtypeId={detailRow.adtype_id}
          adtypeName={detailRow.adtype_name}
          deliveryDateFrom={formattedFrom}
          deliveryDateTo={formattedTo}
        />
      )}
    </div>
  );
};

export default AdReccoView;
